import { SublayerConfig } from "fond/types/ProjectLayerConfig";

/**
 * Builds the editable sublayers metadata from a set of layer/sublayer IDs.
 * Extracts sublayer configuration including parent layer ID and filter expressions.
 */
export const buildEditableSublayers = (
  editingLayerIds: Set<string>,
  config: any
): Array<{
  sublayerId: string;
  parentLayerId: string;
  filter?: import("mapbox-gl").ExpressionSpecification;
}> => {
  const editableSublayers: Array<{
    sublayerId: string;
    parentLayerId: string;
    filter?: import("mapbox-gl").ExpressionSpecification;
  }> = [];

  Array.from(editingLayerIds).forEach((id) => {
    const sublayerConfig = config?.Data.entities[id] as SublayerConfig | undefined;
    if (sublayerConfig?.Type === "SUBLAYER" && sublayerConfig.Key) {
      editableSublayers.push({
        sublayerId: id,
        parentLayerId: sublayerConfig.Key,
        filter: sublayerConfig.FilterConfiguration?.Mapbox,
      });
    }
  });

  return editableSublayers;
};

/**
 * Maps style IDs from context to their actual map layer IDs with .cold/.hot suffixes.
 * Mapbox GL Draw appends .cold or .hot to style IDs when adding them to the map.
 *
 * @param layerStyleIds - Map of layer/sublayer ID to their style IDs
 * @param drawLayers - Array of draw layers from the map
 * @returns Map of style ID to array of actual map layer IDs (with .cold/.hot suffixes)
 */
export const mapStyleIdsToMapLayerIds = (layerStyleIds: Record<string, string[]>, drawLayers: Array<{ id: string }>): Record<string, string[]> => {
  const allStyleIds = Object.values(layerStyleIds).flat();
  const drawLayerIds = new Set(drawLayers.map((l) => l.id));

  const styleIdToMapLayerIds: Record<string, string[]> = {};

  allStyleIds.forEach((styleId) => {
    const matchingLayers: string[] = [];

    // Check if there's a .cold or .hot version
    if (drawLayerIds.has(`${styleId}.cold`)) {
      matchingLayers.push(`${styleId}.cold`);
    }
    if (drawLayerIds.has(`${styleId}.hot`)) {
      matchingLayers.push(`${styleId}.hot`);
    }

    if (matchingLayers.length > 0) {
      styleIdToMapLayerIds[styleId] = matchingLayers;
    }
  });

  return styleIdToMapLayerIds;
};

/**
 * Updates the visibility of map layers based on the layer visibility state.
 * Handles both .cold and .hot versions of each style ID.
 *
 * @param map - Mapbox map instance
 * @param layerStyleIds - Map of layer/sublayer ID to their style IDs
 * @param visibleLayers - Set of layer IDs that should be visible
 * @param styleIdToMapLayerIds - Map of style ID to actual map layer IDs
 */
export const updateLayerVisibility = (
  map: mapboxgl.Map,
  layerStyleIds: Record<string, string[]>,
  visibleLayers: Set<string>,
  styleIdToMapLayerIds: Record<string, string[]>
): void => {
  Object.entries(layerStyleIds).forEach(([layerId, styleIds]) => {
    const isVisible = visibleLayers.has(layerId);

    styleIds.forEach((styleId) => {
      const mapLayerIds = styleIdToMapLayerIds[styleId];
      if (mapLayerIds) {
        mapLayerIds.forEach((mapLayerId) => {
          try {
            map.setLayoutProperty(mapLayerId, "visibility", isVisible ? "visible" : "none");
          } catch (error) {
            console.warn(`Could not update visibility for ${mapLayerId}:`, error);
          }
        });
      }
    });
  });
};
